import { useEffect, useState } from 'react'
import NameIcon from 'app/components/shared/NameIcon'
import Spinner from 'app/components/Spinner'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { bg } from 'lib/background'
import { run } from 'lib/run'

let fetching: Record<string, Promise<string>> = {}
let cache: Record<string, string> = {}

/**
 * Fetches and caches a provider name by cache key and URL.
 * Shared by all vendor-specific lazy provider components.
 *
 * @param parseName - Optional custom parser for the response JSON.
 *   Defaults to reading `data.name`. Sikka returns `{ firstname, lastname }`
 *   so it passes a custom parser to compose the full name.
 */
export async function fetchProviderName(
  cacheKey: string,
  url: string,
  parseName: (data: Record<string, string>) => string = (data) => data?.name || ``,
) {
  if (!cacheKey) return ``
  if (cache[cacheKey] != null) return cache[cacheKey]
  if (fetching[cacheKey] != null) return fetching[cacheKey]
  return (fetching[cacheKey] = run(async () => {
    // Resource-style: /api/providers/resource?kollaConsumerId=...&resourceName=...
    const resourceMatch = url.match(/\/api\/providers\/resource\?kollaConsumerId=([^&]+)&resourceName=([^&]+)/)
    if (resourceMatch) {
      const [consumerId, resourceName] = [resourceMatch[1]!, resourceMatch[2]!].map((value) =>
        decodeURIComponent(value).replace(/["\\]/g, `\\$&`),
      )
      const result = await gqlFetchList<{ id: string; displayName: string }>(
        'providers',
        { search: `kollaConsumerId:"${consumerId}" resourceName:"${resourceName}"` },
        'id displayName',
      )
      return (cache[cacheKey] = result.hits[0]?.displayName ?? ``)
    }
    // Extract provider IDs from the URL to use GQL
    const idsMatch = url.match(/ids=([^&]+)/)
    if (idsMatch) {
      // Kolla-style: /api/providers/name?ids=... — ids can be a comma-joined list
      // (multi-provider transactions). The Provider model exposes displayName, not
      // name; the API silently drops unknown fields instead of erroring.
      const ids = decodeURIComponent(idsMatch[1])
        .split(`,`)
        .map((s) => s.trim())
        .filter(Boolean)
      const search = ids.map((id) => `id:${id}`).join(` OR `)
      const result = await gqlFetchList<{ id: string; displayName: string }>('providers', { search }, 'id displayName')
      const names = result.hits
        .map((p) => p.displayName)
        .filter(Boolean)
        .join(`, `)
      return (cache[cacheKey] = names)
    }
    // Sikka-style: /api/providers/{officeId}/{practiceId}/{providerId}
    const sikkaMatch = url.match(/\/api\/providers\/([^/]+)\/([^/]+)\/([^/]+)/)
    if (sikkaMatch) {
      const result = await gqlFetchList<Record<string, string>>(
        'providers',
        { search: `sikkaOfficeId:${sikkaMatch[1]} sikkaPracticeId:${sikkaMatch[2]} providerId:${sikkaMatch[3]}` },
        'id name firstname lastname',
      )
      const provider = result.hits[0]
      return (cache[cacheKey] = provider ? parseName(provider) : ``)
    }
    // Fallback to REST for unknown URL patterns
    return window.fetch(url).then(async (response) => {
      if (response.status === 404) return (cache[cacheKey] = ``)
      let data = await response.json()
      return (cache[cacheKey] = parseName(data))
    })
  }))
}

export function getCachedName(cacheKey: string) {
  return cache[cacheKey]
}

type LazyProviderProps = {
  cacheKey: string
  fetchName: () => Promise<string>
  showIcon?: boolean
  showSpinner?: boolean
  hideWith?: boolean // omit the "with" prefix, e.g. under an explicit "Provider" label
}

/**
 * Base component for rendering a lazy-loaded provider name.
 * Vendor-specific components (Sikka, Kolla, Carestack) wrap this
 * with their own fetch logic and cache key generation.
 */
export function LazyProvider({ cacheKey, fetchName, showIcon, showSpinner, hideWith }: LazyProviderProps) {
  const [name, setName] = useState<string>(cacheKey ? cache[cacheKey] : ``)
  const [loading, setLoading] = useState<boolean>(!!cacheKey && cache[cacheKey] == null)

  useEffect(() => {
    if (!cacheKey) return
    if (name && cache[cacheKey] === name) return
    setName(``)
    setLoading(true)
    bg(
      fetchName().then((resolved) => {
        setName(resolved)
        setLoading(false)
      }),
    )
  }, [cacheKey])

  if (!cacheKey) return null

  if (loading && !name) return <>{showSpinner && <Spinner size={5} />}</>

  if (!name) return null

  return (
    <div className="flex items-center gap-x-1">
      {showIcon && (
        <>
          <span className="text-body-sm text-minimal">with</span>
          <NameIcon
            widthClassName="w-5"
            heightClassName="h-5"
            backgroundClassName="bg-surface-strong  border border-strong rounded-full"
            textClassName="text-strong"
            fullName={`${name[0]}`}
            sizeStyle={{ fontSize: `8px` }}
          />
        </>
      )}
      {!showIcon && !hideWith && <span className="text-body-sm text-minimal">with</span>}
      <span
        className={
          showIcon ? `` : `font-semibold max-w-[450px] inline-block text-ellipsis whitespace-nowrap overflow-hidden`
        }
      >
        {name}
      </span>
    </div>
  )
}
